import device from 'common/support/device';
import {
  getAmpersandText,
  getDotToIgnoreText,
  getDotText,
  getSlashText,
  getDashText,
  getAllCapsText,
  getCBSText,
  getHTMLFilteredText,
  getNoAsNumberText,
  getInitialOverrides,
  getFinalOverrides,
  getTimeText,
  getHttpPunctuationText,
  removeColons,
} from 'support/textToSpeechHelper';
import config from 'config';
import { IntlShape } from 'react-intl';
import userGestureManager from 'support/focus/UserGestureManager';

export const ttsInitEventName = 'ttsInitEvent';

const ALL_CAPS_TEXT_TO_SPLIT = config.textToSpeech.allCapsTextToSplit;

class TextToSpeechManager {
  static instance: TextToSpeechManager;

  ttsMap: Record<string, string> = {};

  queue: string[] = [];

  ttsInit: boolean = false;

  ttsEnabled: boolean = true;

  intl?: IntlShape;

  register = (id: string, value: string): void => {
    this.ttsMap[id] = value;
  };

  unregister = (id: string): void => {
    delete this.ttsMap[id];
  };

  getTTSText = (id: string): string => {
    return this.ttsMap[id];
  };

  constructor(intl?: IntlShape) {
    if (!TextToSpeechManager.instance) {
      document.addEventListener(
        ttsInitEventName,
        () => {
          this.ttsInit = true;
          this.speak();
        },
        false,
      );
      TextToSpeechManager.instance = this;
      userGestureManager.addGestureListener(() => TextToSpeechManager.instance.speak());
    }
    if (intl && intl !== TextToSpeechManager.instance.intl) {
      TextToSpeechManager.instance.intl = intl;
    }

    return TextToSpeechManager.instance;
  }

  add(item: string | string[] | null | undefined): void {
    if (item) {
      this.queue = this.queue.concat(item);
    }
  }

  unshift(item: string): void {
    this.queue.unshift(item);
  }

  clear(): void {
    this.queue = [];
  }

  constructText(): string {
    return this.queue.map(text => this.getPhoneticText(text)).join('. ');
  }

  speak = (): void => {
    if (this.ttsInit && this.ttsEnabled) {
      if (device.requiresUserGestureForSpeech && !userGestureManager.hasUserGesture) {
        this.clear();
        return;
      }
      // console.info('[TTS]', this.constructText());
      if (!this.constructText()) {
        this.clear();

        return;
      }
      device.startTTS(this.constructText());
      this.clear();
    }
  };

  stop = (): void => {
    device.stopTTS();
  };

  getPhoneticText = (text: string): string => {
    let result = text;
    result = getDotToIgnoreText(result);
    if (this.intl) {
      result = getInitialOverrides(result, this.intl);

      result = getHttpPunctuationText(result, this.intl);
      result = getSlashText(result, this.intl);

      result = getNoAsNumberText(result, this.intl);
      result = getDotText(result, this.intl);

      result = getAmpersandText(result, this.intl);
      result = getDashText(result, this.intl);
      result = getTimeText(result, this.intl);

      // Handle special cases at the end where previous helpers would cause undesired transformations
      result = getFinalOverrides(result, this.intl);
    }

    result = removeColons(result);
    result = getAllCapsText(result, ALL_CAPS_TEXT_TO_SPLIT);
    result = getCBSText(result);
    result = getHTMLFilteredText(result);
    return result;
  };
}

export const initializeTextToSpeechManager = (intl: IntlShape): void => {
  // eslint-disable-next-line no-new
  new TextToSpeechManager(intl);
};

export default new TextToSpeechManager();
